import {useCallback, useEffect, useState} from "react";
import {NavigateAction, SlotInfo, View} from "react-big-calendar";
import {EventInteractionArgs} from "react-big-calendar/lib/addons/dragAndDrop";
import {useDispatch, useSelector} from "react-redux";
import {useQuery} from "../../../utils/useQuery";
import {selectAccount} from "../../../redux/accountSlice";
import {useNavigate} from "react-router-dom";
import {getSchedules, selectSchedules} from "../../../redux/schedulesSlice";
import {getWeeklySchedules, selectWeeklySchedules} from "../../../redux/weeklySchedulesSlice";
import {selectShops} from "../../../redux/shopsSlice";
import {selectActiveEmployees} from "../../../redux/employeesSlice";
import {find, flattenDeep, includes, some} from "lodash";
import {Path} from "app/path";
import {CrudAppointmentActions} from "./useSchedules";
import {addDays, addMinutes, addMonths, differenceInMinutes, format, subDays, subMonths, toDate} from "date-fns";
import {AvailabilityStatusTypes} from "../../../redux/availabilitiesSlice";
import {adjustDateToUTC} from "../../../utils/formatTime";
import {AllowedView} from "../MyCalendar";
import {getMonthlySchedules} from "../../../redux/monthlySchedulesSlice";
import {addHours} from "date-fns";
import { setActiveShopTimeZone, shopNow, toLocalDate } from "../../../utils/timezone";
import { BookingPart, getBookingParts } from "../utils/bookingParts";

export interface CalendarEventBase {
    id: number;
    title: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    color?: string;
    resourceId: number; // 👈 link to a specific resource
}

export interface AppointmentEvent extends CalendarEventBase {
    type: "appointment";
    customerName: string;
    service: string;
    price?: number;
    status: "booked" | "completed" | "cancelled";
    client:any,
    employee: any,
    shop: any,
    services:any,
    appointment:any,
    sale:any,
    bookingPart?: BookingPart,










}

export interface BlockedEvent extends CalendarEventBase {
    type: "blocked";
    reason: string; // e.g., "Lunch Break", "Maintenance", "Staff Meeting"
}

export interface OffTimeEvent extends CalendarEventBase {
    type: "offtime";
    dayOfWeek?: number;
    recurring?: boolean;
    note?: string;
}

export type MyCalendarEvent =
    | AppointmentEvent
    | BlockedEvent
    | OffTimeEvent;


export interface UseCalendarStateOptions<TEvent extends CalendarEventBase = CalendarEventBase> {
    defaultEvents?: TEvent[];
    defaultDate?: Date;
    defaultView?: View;
    onEventsChange?: (events: TEvent[]) => void;
}


export interface ResourceProps {
    resourceId: number;
    resourceTitle: string;
    avatar?: string;
}

interface EmployeeSchedule {
    employeeId: number;
    employeeName: string;
    employeeAvatar: string | null;
    shopIds: number[];
    date: string;
    availability?: {
        id: number;
        type: string;
        dayOrDate: string;
        timeStart: string;
        timeEnd: string;
        status: "scheduled" | "off" | string;
        startOfWeek: string;
    };
    appointments: any[];
    blockedTimes: any[];
}


export function useCalendarState() {
    const dispatch = useDispatch();
    const query = useQuery();
    const account = useSelector(selectAccount);
    const navigate = useNavigate();
    const schedules = useSelector(selectSchedules);
    const weeklySchedules = useSelector(selectWeeklySchedules);
    const shops = useSelector(selectShops);
    const [isLoading, setIsLoading] = useState(false);
    const [currentShopTime, setCurrentShopTime] = useState(() => shopNow());
    const employees = useSelector(selectActiveEmployees);
    const selectedShopId = query.get("shopId");
    const shop = find(shops, {id: Number(selectedShopId)});
    if (shop?.timezone?.timeZoneId) {
        setActiveShopTimeZone(shop.timezone.timeZoneId);
    }

    const selectedEmployeeId = query.get("employeeId")
        ? parseInt(query.get("employeeId")!)
        : employees[0]?.id;
    const [selectedEmployee, setSelectedEmployee] = useState<any>(
        find(employees, {id: selectedEmployeeId})
    );


    // const [selectedWeeklyEmployee, setSelectedWeeklyEmployee] = useState<any>(
    //     () => {
    //         const selectedId = query.get("employeeId")
    //             ? parseInt(query.get("employeeId")!)
    //             : null;
    //         return find(employees, { id: selectedId });
    //     }
    // );

    const dateParam = query.get("date");
    const [date, setDate] = useState<Date>(
        // Parse ?date= as LOCAL midnight (adjustDateToUTC), not UTC midnight. Raw new Date("yyyy-MM-dd")
        // is UTC midnight, which renders as the PREVIOUS day in any behind-UTC timezone — so the
        // displayed day, the day queried from the API, and where toLocalDate() places events all drift
        // apart, and a freshly-booked appointment lands on the adjacent day / outside the query window.
        dateParam ? adjustDateToUTC(dateParam) : currentShopTime
    );
    const [appointments, setAppointments] = useState<any[]>([]);
    const [resourceMap, setResourceMap] = useState<ResourceProps[]>([]);
    const [pendingAppointment, setPendingAppointment] = useState<any>();
    const [isDoingPayment, setIsDoingPayment] = useState(false)

    const [selectedAppointmentEvent, setSelectedAppointmentEvent] =
        useState(null);
    const [appointmentDrawer, setAppointmentDrawer] = useState({
        isOpen: false,
        type: null,
    } as { isOpen: boolean; type: CrudAppointmentActions | null });


    const [events, setEvents] = useState<MyCalendarEvent[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
    const [showBookingModal, setShowBookingModal] = useState<boolean>(false)
    const [showQuickCheckout, setShowQuickCheckout] = useState<boolean>(false)

    const [calendarView, setCalendarView] = useState<"day" | "week" | "month">(() => 'day');

    const [blockedTimes] = useState<MyCalendarEvent[]>([

        // {
        //     id: 1,
        //     type: "blocked",
        //     title: "Lunch Break",
        //     start: new Date(2025, 10, 10, 13, 0),
        //     end: new Date(2025, 10, 10, 14, 0),
        //     reason: "Lunch",
        //     color: "#9ca3af",
        //     resourceId: 1,
        // },
        // {
        //     id: 1,
        //     type: "offtime",
        //     title: "Sunday Off",
        //     start: new Date(2025, 10, 9, 0, 0),
        //     end: new Date(2025, 10, 9, 23, 59),
        //     recurring: true,
        //     dayOfWeek: 0,
        //     note: "Weekly off",
        //     color: "#f87171",
        //     resourceId: 1,
        // },
    ]);


    const isOverlapping = (startA: Date, endA: Date, startB: Date, endB: Date) => {
        // Convert to timestamps to avoid timezone precision issues
        const startATime = startA.getTime();
        const endATime = endA.getTime();
        const startBTime = startB.getTime();
        const endBTime = endB.getTime();

        // Overlap check
        return startATime < endBTime && endATime > startBTime;
    };

    const isBlockedTime = (start: Date, end: Date, resourceId?: number): boolean => {
        return blockedTimes.some((block: any) => {
            // Check resource-specific block
            if (!resourceId) return false
            if (block.resourceId !== resourceId) return false;

            // Handle recurring weekly block (like Sunday)
            if (block.recurring && block.dayOfWeek !== undefined) {
                const dayOfWeek = start.getDay(); // 0 = Sunday
                if (dayOfWeek === block.dayOfWeek) {
                    return true; // Entire day blocked for this resource
                }
            }

            // Regular date/time overlap
            return isOverlapping(start, end, block.start, block.end);
        });
    };


    const normalizeWeeklySchedules = (weeklySchedules: any) => {
        const normalizedSchedules: any[] = [];
        if (!weeklySchedules || !weeklySchedules.data) {
            return normalizedSchedules;
        }

        // Each schedule in data represents a day's schedule
        weeklySchedules.data.forEach((daySchedule: any) => {
            normalizedSchedules.push({
                employeeId: Number(daySchedule.employeeId),
                employeeName: daySchedule.employeeName,
                employeeAvatar: daySchedule.employeeAvatar,
                shopIds: daySchedule.shopIds || [],
                date: daySchedule.start?.split("T")[0] || daySchedule.date,
                availability: daySchedule.availability || {
                    timeStart: "09:00",
                    timeEnd: "17:00",
                    status: AvailabilityStatusTypes.SCHEDULED,
                },
                appointments: daySchedule.appointments || [],
                blockedTimes: daySchedule.blockedTimes || [],
            });
        });

        return normalizedSchedules;
    };


    const getSchedulesOfEmployees = async ({
                                               currentDate = currentShopTime,
                                               specificEmployeeId,
                                           }: {
        currentDate?: Date;
        specificEmployeeId?: number;
    }) => {
        const currentParams = new URLSearchParams(window.location.search);
        const currentShopId = currentParams.get("shopId");

        const employeeIds = employees
            .filter((employee) =>
                some(employee.shops, (shop) => shop.id === Number(currentShopId))
            )
            .map((employee) => employee.id);

        try {
            const newSchedules = await dispatch(
                getSchedules({
                    employeeIds: specificEmployeeId ? [specificEmployeeId] : employeeIds,
                    date: currentDate,
                }) as any
            ).unwrap();

            calendarInit(newSchedules, currentDate);
        } catch (error) {
            console.log("Error fetching schedules:", error);
        }
    };




    const getWeeklySchedulesOfEmployees = async ({
                                                     employeeId,
                                                     date,
                                                 }: {
        employeeId: number;
        date: Date;
    }) => {
        setIsLoading(true);
        navigate(`${Path.HOME}?${query.toString()}`);


        console.log('schedule for date--',date)
        try {
            const weeklySchedules = await dispatch(
                getWeeklySchedules({
                    employeeId,
                    date,
                }) as any
            ).unwrap();

            // Only update the UI if we're still in weekly view
            if (calendarView === "week") {
                calendarInit(normalizeWeeklySchedules(weeklySchedules), date);
            }
        } catch (error) {
            console.log("Error fetching weekly schedules:", error);
        } finally {
            setIsLoading(false);
        }
    };
    const getMonthlySchedulesOfEmployees = async ({
                                                     date,
                                                      employeeId,
                                                 }: {

        date: Date;
        employeeId?: number;
    }) => {
        setIsLoading(true);
        navigate(`${Path.HOME}?${query.toString()}`);

        try {
            const monthlySchedules = await dispatch(
                getMonthlySchedules({
                    employeeId,
                    date,
                }) as any
            ).unwrap();

            // Only update the UI if we're still in weekly view
            if (calendarView === "month") {
                console.log('month schedules',monthlySchedules)
                 calendarInit(normalizeWeeklySchedules(monthlySchedules), date);
            }
        } catch (error) {
            console.log("Error fetching monthly schedules:", error);
        } finally {
            setIsLoading(false);
        }
    };


    const updateCalendarData = async (dateOverride?: Date,viewOverride?:string) => {
        // dateOverride is used to update the calendar data for a specific date
        const dateToUse = dateOverride || date;
        const viewToUse = viewOverride || calendarView;


        try {
            setIsLoading(true);
            if (viewToUse === "week") {
                // setSelectedEmployee(null);

                if (selectedEmployee?.id && dateToUse) {
                    query.set("employeeId", selectedEmployee.id.toString());
                    query.set("calendarView", "week");
                    navigate(`${Path.HOME}?${query.toString()}`);
                    await getWeeklySchedulesOfEmployees({
                        employeeId: selectedEmployee.id,
                        date: dateToUse,
                    });
                } else
                    return;
            }

            if (!selectedEmployee && employees.length > 0) {
                setSelectedEmployee(employees[0]);
            }

            if (viewToUse === "day") {
                await getSchedulesOfEmployees({
                    currentDate: dateToUse,
                });
            }
            if (viewToUse === "month") {
                await getMonthlySchedulesOfEmployees({
                    date: dateToUse,
                    employeeId: selectedEmployee.id,
                });
            }


        } catch (err) {
            console.log("Error updating calendar data:", err);
        } finally {
            setIsLoading(false);
        }
    };


    const handleNavigate = useCallback(
        (newDate: Date, newView: AllowedView, action: NavigateAction) => {
            let updatedDate = new Date(newDate);

            switch (action) {
                case "TODAY":
                    updatedDate = shopNow();
                    break;

                case "PREV":
                    updatedDate =
                        newView === "month"
                            ? subMonths(date, 1)
                            : newView === "week"
                                ? subDays(date, 7)
                                : subDays(date, 1);
                    break;

                case "NEXT":
                    updatedDate =
                        newView === "month"
                            ? addMonths(date, 1)
                            : newView === "week"
                                ? addDays(date, 7)
                                : addDays(date, 1);

                    break;


                case "DATE":
                    updatedDate = toDate(date)

                    break;
                default:
                    updatedDate = newDate;
                    break;
            }

            console.log('here', updatedDate)

            try {
                // Update URL and state first
                query.delete("date");
                query.delete("appointmentId");
                query.delete("phantomId");
                query.set("date", format(updatedDate, "yyyy-MM-dd"));
                query.set("calendarView", calendarView);


                navigate(`${Path.HOME}?${query.toString()}`);


            } catch (error) {
                console.error("Error during weekly navigation:", error);
            }


            setDate(updatedDate);
            updateCalendarData(updatedDate);
        },
        [date, calendarView, updateCalendarData]
    );


    const handleViewChange = useCallback((newView: 'day' | 'month' | 'week') => {
        query.set("calendarView", newView);
        const date = query.get('date')
        setDate(date ? adjustDateToUTC(date) : shopNow())
        const empId = query.get("employeeId");


        if (empId) {
            const sm = find(employees, {id: parseInt(empId)})
            setSelectedEmployee(sm)

        } else {
            setSelectedEmployee(employees[0])
        }

        navigate(`${Path.HOME}?${query.toString()}`);
        setCalendarView(newView)


        console.log('new view', newView)
    }, [calendarView]);

    const handleSelectEvent = useCallback((event: MyCalendarEvent) => {
        setIsDoingPayment(false);
        setSelectedEvent(event);
        setAppointmentDrawer({isOpen:true,type:CrudAppointmentActions.READ})
    }, []);


    const createNewPendingAppointment = (event: any) => {
        query.delete("appointmentId");
        query.delete("phantomId");
        // Ensure calendarView param is preserved
        query.set("calendarView", calendarView);
        navigate(`${Path.HOME}?${query.toString()}`);
        const newAppointment = {
            employee: event.employee,
            services: event.services,
            client: event.client,
            title: "New Appointment",
            start: event.start,
            end:
                differenceInMinutes(event.end, event.start) < 40
                    ? addMinutes(event.start, 40)
                    : event.end,
            resourceId: [event.employee.id],
            pending: true,
        };
        setPendingAppointment(newAppointment);
        // setSelectedBlockTime({ ...newAppointment, title: "Break" });
    };

    const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
        console.log("Slot clicked", slotInfo);
        const em = find(employees, {id: slotInfo.resourceId})
        // setSelectedEmployee(em)

        if (slotInfo.action === "doubleClick") return;

        // Avoid invalid ranges
        if (!slotInfo.start || !slotInfo.end || slotInfo.start >= slotInfo.end) {
            console.log("Invalid slot range");
            return;
        }

        // Prevent handling the same slot twice within a short window
        const startTime = slotInfo.start.getTime();
        const endTime = slotInfo.end.getTime();
        if (startTime === endTime) {
            console.log("Zero-length slot — skip");
            return;
        }

        // ✅ Blocked time check
        if (slotInfo.resourceId && isBlockedTime(slotInfo.start, slotInfo.end, +slotInfo?.resourceId)) {
            console.log("Blocked slot detected", slotInfo);
            return;
        }

        const employeeId = Array.isArray(slotInfo.resourceId)
            ? Number(slotInfo.resourceId[0])
            : Number(slotInfo.resourceId);

        const employee = employees.find((emp) => emp.id === employeeId);
        if (!employee) {
            console.log("No employee found");
            return;
        }

        createNewPendingAppointment({
            title: "New Appointment",
            employee,
            services: [],
            client: null,
            start: slotInfo.start,
            end: slotInfo.end,
        });

        setShowBookingModal(true);
    }, [employees]);


    const handleEventDrop = useCallback(
        ({event, start, end, isAllDay, resourceId}: EventInteractionArgs<MyCalendarEvent>) => {
            const newStart = new Date(start);
            const newEnd = new Date(end);

            if (resourceId && isBlockedTime(newStart, newEnd, +resourceId)) {
                alert("You cannot move events into blocked time (e.g. Lunch or Off Hours).");
                return;
            }

            setEvents((prev): MyCalendarEvent[] =>
                prev.map(ev =>
                    ev.id === event.id
                        ? ({
                            ...ev,
                            start: newStart,
                            end: newEnd,
                            allDay: isAllDay ?? ev.allDay,
                        } as MyCalendarEvent)
                        : ev
                )
            );
        },
        [setEvents, isBlockedTime]
    );

    const handleEventResize = useCallback(
        ({event, start, end, isAllDay, resourceId}: EventInteractionArgs<MyCalendarEvent>) => {
            const updated = events.map(ev =>
                ev.id === event.id ? {...ev, start, end, allDay: isAllDay} as MyCalendarEvent : ev
            );
            setEvents(updated);
            // onEventsChange?.(updated);
        },
        [events]
    );


    useEffect(() => {
        console.log('events', events)
    }, [events]);

    const goToToday = useCallback(() => setDate(shopNow()), []);


    // const getMappedAppointments = (employeeSchedulesArray: any[]) => {
    //     const mappedAppointments: any[] = [];
    //     console.log('employeeSchedulesArray',employeeSchedulesArray)
    //
    //     employeeSchedulesArray.forEach(
    //         ({ employeeId, appointments, availability, blockedTimes, date,id }: any) => {
    //             // For each schedule, create the appropriate events
    //
    //             // First handle availability status
    //             if (availability?.status !== AvailabilityStatusTypes.SCHEDULED) {
    //                 // If not scheduled, mark the entire day as OFF
    //                 mappedAppointments.push({
    //
    //                     type: OtherEventTypes.OFF,
    //                     start: new Date(`${date}T00:00:00`),
    //                     end: new Date(`${date}T23:59:59`),
    //                     resourceId: [employeeId],
    //                     pending: false,
    //                     employee: employees.find((emp) => emp.id === employeeId),
    //
    //                 });
    //             } else {
    //                 // Handle scheduled availability with proper time windows
    //                 if (availability.timeStart) {
    //                     // Add OFF time from midnight to start of availability
    //                     mappedAppointments.push({
    //                         type: OtherEventTypes.OFF,
    //                         start: new Date(`${date}T00:00:00`),
    //                         end: new Date(`${date}T${availability.timeStart}:00`),
    //                         resourceId: [employeeId],
    //                         pending: false,
    //                         employee: employees.find((emp) => emp.id === employeeId),
    //                     });
    //                 }
    //
    //                 if (availability.timeEnd) {
    //                     // Add OFF time from end of availability to midnight
    //                     mappedAppointments.push({
    //                         type: OtherEventTypes.OFF,
    //                         start: new Date(`${date}T${availability.timeEnd}:00`),
    //                         end: new Date(`${date}T23:59:59`),
    //                         resourceId: [employeeId],
    //                         pending: false,
    //                         employee: employees.find((emp) => emp.id === employeeId),
    //                     });
    //                 }
    //             }
    //
    //             // Then handle blocked times
    //             if (blockedTimes?.length > 0) {
    //                 blockedTimes.forEach((blockTime: any) => {
    //                     const start = preserveUtcTimeToLocal(blockTime.startTime);
    //                     const end = preserveUtcTimeToLocal(blockTime.endTime);
    //
    //                     mappedAppointments.push({
    //                         id: blockTime.id,
    //                         title: blockTime.name,
    //                         start,
    //                         end,
    //                         type: OtherEventTypes.BLOCKED,
    //                         resourceId: [employeeId],
    //                         pending: false,
    //                         employee: employees.find((emp) => emp.id === employeeId),
    //                         blockTimeData: blockTime,
    //                     });
    //                 });
    //             }
    //
    //             // Finally handle appointments
    //             if (appointments?.length > 0) {
    //                 appointments.forEach((appointment: any) => {
    //                     const start = preserveUtcTimeToLocal(appointment.startTime);
    //                     const end = preserveUtcTimeToLocal(appointment.endTime);
    //
    //                     console.log('appointments mapping',appointment)
    //                     mappedAppointments.push({
    //                         id:appointment.id,
    //                         services: appointment.services,
    //                         client: appointment.client,
    //                         title: `${appointment?.services?.[0]?.title}`,
    //                         start,
    //                         end,
    //                         type: "appointment",
    //                         appointment,
    //                         sale:appointment.sale,
    //                         resourceId: [employeeId],
    //                         shop: appointment.shop,
    //                         pending: false,
    //                         employee: employees.find((emp) => emp.id === employeeId),
    //                     });
    //                 });
    //             }
    //         }
    //     );
    //
    //     return mappedAppointments;
    // };


    const getMappedCalendarEvents = (
        employeeSchedules: EmployeeSchedule[]
    ): MyCalendarEvent[] => {
        const mappedEvents: MyCalendarEvent[] = [];
        // Services of one multi-service booking, across all barbers shown: "1 of 2", "2 of 2"
        const bookingParts = getBookingParts(employeeSchedules.flatMap((s) => s.appointments || []));

        employeeSchedules.forEach((schedule) => {
            const {
                employeeId,
                employeeName,
                employeeAvatar,
                date,
                availability,
                appointments,
                blockedTimes,
            } = schedule;

            const employeeResource = {
                id: employeeId,
                title: employeeName,
                avatar: employeeAvatar || undefined,
            };

            const dayStart = new Date(`${date}T00:00:00`);
            const dayEnd = new Date(`${date}T23:59:59`);

            // 1️⃣ OFF-TIME MAPPING (before and after availability)
            if (availability?.status === "scheduled") {
                const availStart = new Date(`${date}T${availability.timeStart}:00`);
                const availEnd = new Date(`${date}T${availability.timeEnd}:00`);

                // Before shift
                if (availability.timeStart) {
                    mappedEvents.push({
                        id: Number(`${availability.id}1`),
                        type: "offtime",
                        title: "Off time",
                        start: dayStart,
                        end: availStart,
                        color: "rgba(137,135,135,0.49)",
                        resourceId: employeeId,
                        note: "Outside working hours",
                    } as OffTimeEvent);
                }

                // After shift
                if (availability.timeEnd) {
                    mappedEvents.push({
                        id: Number(`${availability.id}2`),
                        type: "offtime",
                        title: "Off time",
                        start: availEnd,
                        end: dayEnd,
                        color: "rgba(137,135,135,0.49)",
                        resourceId: employeeId,
                        note: "Outside working hours",
                    } as OffTimeEvent);
                }
            } else {
                // Full-day off
                mappedEvents.push({
                    id: Number(`${employeeId}${date.replace(/-/g, "")}`),
                    type: "offtime",
                    title: "Day Off",
                    start: dayStart,
                    end: dayEnd,
                    color: "#f87171",
                    resourceId: employeeId,
                    note: "Not scheduled",
                } as OffTimeEvent);
            }

            // 2️⃣ BLOCKED TIMES
            if (blockedTimes?.length > 0) {
                blockedTimes.forEach((block) => {
                    const start = toLocalDate(block.startTime);
                    const end = toLocalDate(block.endTime);

                    mappedEvents.push({
                        id: block.id,
                        type: "blocked",
                        title: block.name || "Blocked",
                        start,
                        end,
                        reason: block.reason || "Blocked time",
                        color: "#9ca3af", // gray
                        resourceId: employeeId,
                    } as BlockedEvent);
                });
            }

            // 3️⃣ APPOINTMENTS
            if (appointments?.length > 0) {
                appointments.forEach((appointment) => {
                    console.log('appointment in mapping',appointment)
                    const start = toLocalDate(appointment.startTime);
                    const end = toLocalDate(appointment.endTime);
                    const services = appointment.services;


                    mappedEvents.push({
                        id: appointment.id,
                        type: "appointment",
                        start,
                        end,
                        title: `${appointment?.services?.[0]?.title}`,
                        client:appointment.client,
                        employee: employees.find((emp) => emp.id === employeeId),
                        shop: appointment.shop,
                        services:services,
                        appointment:appointment,
                        sale: appointment.sale ? { ...appointment.sale } : null,
                        bookingPart: bookingParts.get(appointment.id),
                        status:
                            appointment.sale?.status === "OPEN"
                                ? "booked"
                                : appointment.sale?.status === "CLOSED"
                                    ? "completed"
                                    : "cancelled",
                        // color: "#4F46E5",
                        resourceId: employeeId,
                    } as AppointmentEvent);
                });
            }
        });

        return mappedEvents;
    };
    const getMappedCalendarMonthlyEvents = (
        employeeSchedules: EmployeeSchedule[]
    ): MyCalendarEvent[] => {
        const mappedEvents: MyCalendarEvent[] = [];

        employeeSchedules.forEach((schedule) => {
            const { employeeId, employeeName, employeeAvatar, appointments } = schedule;

            if (!appointments?.length) return;

            const employeeResource = {
                id: employeeId,
                title: employeeName,
                avatar: employeeAvatar || undefined,
            };

            // ✅ Only map appointment events
            appointments.forEach((appointment) => {
                const start = toLocalDate(appointment.startTime);
                const end = toLocalDate(appointment.endTime);
                const services = appointment.services;

                mappedEvents.push({
                    id: appointment.id,
                    type: "appointment",
                    start,
                    end,
                    title: services?.[0]?.title || "Appointment",
                    client: appointment.client,
                    employee: employees.find((emp) => emp.id === employeeId),
                    shop: appointment.shop,
                    services,
                    appointment,
                    sale: appointment.sale ? { ...appointment.sale } : null,
                    status:
                        appointment.sale?.status === "OPEN"
                            ? "booked"
                            : appointment.sale?.status === "CLOSED"
                                ? "completed"
                                : "cancelled",
                    resourceId: employeeId,
                } as AppointmentEvent);
            });
        });

        return mappedEvents;
    };

    const toggleAppointmentDrawer = (
        open: boolean,
        type?: CrudAppointmentActions
    ) => {
        setAppointmentDrawer({ isOpen: open, type: type || null });
    };

    const getMappedResources = (employeeSchedulesArray: any) => {
        const mappedEmployeeResources: ResourceProps[] = [];
        const currentParams = new URLSearchParams(window.location.search);
        const currentShopId = currentParams.get("shopId");
        employeeSchedulesArray.forEach(
            ({
                 employeeId,
                 employeeName,
                 availability,
                 employeeAvatar,
                 appointments,
                 shopIds,
             }: any) => {
                if (!includes(shopIds, Number(currentShopId))) {
                    return;
                }

                mappedEmployeeResources.push({
                    resourceId: employeeId,
                    resourceTitle: employeeName,
                    avatar: employeeAvatar,
                });
            }
        );

        return mappedEmployeeResources;
    };

    const calendarInit = (newSchedules: any[], currentDate: any) => {


        if (!newSchedules) {
            return;
        }
        if (newSchedules.length === 0) {
            setEvents([]); // force React Big Calendar refresh
            return;
        }

        if (calendarView === "day") {
            const mappedResources = getMappedResources(newSchedules);
            setResourceMap(mappedResources);
        }
        if (calendarView === "week") {
            const mappedResources = getMappedResources(newSchedules);
            const weeklyResourceMap = selectedEmployee
                ? [
                    {
                        resourceId: selectedEmployee.id,
                        resourceTitle: selectedEmployee.name,
                        avatar: selectedEmployee.avatar,
                    },
                ] : []

            setResourceMap(weeklyResourceMap);
        }
        if (calendarView === "month") {
            const mappedResources = getMappedResources(newSchedules);

            const weeklyResourceMap = selectedEmployee
                ? [
                    {
                        resourceId: selectedEmployee.id,
                        resourceTitle: selectedEmployee.name,
                        avatar: selectedEmployee.avatar,
                    },
                ] : []

             setResourceMap([]);
        }
        if (calendarView==="month") {
            const mappedEvents = getMappedCalendarMonthlyEvents(newSchedules);
            setEvents(mappedEvents.map(e => ({ ...e  })));
        }
        else {
            const mappedEvents = getMappedCalendarEvents(newSchedules);
            setEvents(mappedEvents.map(e => ({ ...e  })));
        }
    };

    // useEffect(() => {
    //
    //     console.log('pending appintment--', pendingAppointment)
    //
    // }, [pendingAppointment]);


    useEffect(() => {
        if (query.get("appointmentId")) {
            const appointmentId = parseInt(query.get("appointmentId") as string);
            const appointment = find(
                flattenDeep(appointments),
                (appointment) => appointment.appointment?.id === appointmentId
            );
            if (appointment) {
                // setSelectedAppointmentEvent(appointment);
                setSelectedEvent(appointment);
                setAppointmentDrawer({
                    isOpen: true,
                    type: CrudAppointmentActions.READ,
                });
            }
        } else if (query.get("blockTimeId")) {
            const blockTimeId = parseInt(query.get("blockTimeId") as string);
            const blockTime = find(
                flattenDeep(appointments),
                (appointment) => appointment.id === blockTimeId
            );
            if (blockTime) {
                // setSelectedBlockTime(blockTime);
                setAppointmentDrawer({
                    isOpen: true,
                    type: CrudAppointmentActions.BLOCKED,
                });
            }
        }
    }, [appointments]);


    useEffect(() => {
        const currentViewDate = query.get("date");


        if (currentViewDate) {
            updateCalendarData(adjustDateToUTC(currentViewDate));
        }
    }, [calendarView,selectedEmployee?.id]);

    useEffect(() => {
        let date = query.get("date");
        let shopId = query.get("shopId");
        let employeeId = query.get("employeeId");
        let calendarView = query.get("calendarView");

        if (!shopId || !date || !employeeId) {
            console.log('here logged', employeeId)
            if (!shopId) {
                shopId = shops[0]?.id.toString();
                query.set("shopId", shopId);
            }
            if (!date) {
                date = format(shopNow(), "yyyy-MM-dd");
                query.set("date", date);
            }
            if (!employeeId && calendarView === "week") {
                // employeeId = selectedWeeklyEmployee?.id.toString();
                employeeId = selectedEmployee?.id.toString();
                query.set("employeeId", selectedEmployee?.id.toString());
            }
            // if (!calendarView) {
            //     calendarView = "day";
            //     query.set("calendarView", calendarView);
            // }


            navigate(`${Path.HOME}?${query.toString()}`);
        }

        else {
        query.set("shopId", selectedShopId ?? shopId );
        query.set("date", format(shopNow(), "yyyy-MM-dd"));
        employeeId = selectedEmployee?.id.toString();
            if (!employeeId && calendarView === "week") {
                // employeeId = selectedWeeklyEmployee?.id.toString();
                employeeId = selectedEmployee?.id.toString();
                query.set("employeeId", selectedEmployee?.id.toString());
            }

        }


        navigate(`${Path.HOME}?${query.toString()}`);

        if (employeeId) {
            const currentEmployee = find(employees, {id: employeeId})
            setSelectedEmployee(currentEmployee)
        }


        if (calendarView) {
            handleViewChange(calendarView as "day" | "week" | "month")
        }


        // Local-midnight parse (see the useState note above) — was raw new Date(date), which broke day alignment.
        const adjustedDate = adjustDateToUTC(date);
        setDate(adjustedDate);
        // setCurrentShopTime(
        //     toZonedTime(
        //         new Date(),
        //         find(shops, {id: Number(shopId)})?.timezone.timeZoneId
        //     )
        // );
        updateCalendarData(adjustedDate);
    }, [selectedShopId]);

    useEffect(() => {
        setActiveShopTimeZone(shop?.timezone?.timeZoneId);
    }, [shop?.timezone?.timeZoneId]);



    const onNewAppointmentInit = ({
                                      startTime = new Date(date),
                                      endTime = addHours(new Date(date), 1),
                                  }: {
        startTime?: Date;
        endTime?: Date;
    }) => {
        createNewPendingAppointment({
            employee: selectedEmployee ?  selectedEmployee : employees[0],
            client: null,
            start: startTime,
            end: endTime,
            services: [],
        });
        setShowBookingModal(true)

        // toggleAppointmentDrawer(true, CrudAppointmentActions.CREATE);
    };

    const onQuickCheckout = () => {
        setShowQuickCheckout(true)
    };

    return {
        employees,
        date,
        calendarView,
        setCalendarView,
        events,

        setEvents,
        handleNavigate,
        handleViewChange,
        handleSelectEvent,
        handleSelectSlot,
        handleEventDrop,
        handleEventResize,
        goToToday,
        blockedTimes,

        selectedEmployee,
        setSelectedEmployee,

// booking modal
        showBookingModal,
        setShowBookingModal,
        showQuickCheckout,
        setShowQuickCheckout,


//pending appointment
        pendingAppointment,
        setPendingAppointment,


        // appointment drawer
        setAppointmentDrawer,
        appointmentDrawer,

        getWeeklySchedulesOfEmployees,
        getSchedulesOfEmployees,

        resourceMap,
        isLoading,

        shops,
        shop,


        selectedEmployeeId,
        isDoingPayment,
        setIsDoingPayment,
        selectedEvent,
        setSelectedEvent,
        toggleAppointmentDrawer,
        createNewPendingAppointment,
        onNewAppointmentInit,
        onQuickCheckout,
        updateCalendarData
    };
}
